import React, {
	FormEventHandler,
	forwardRef,
	ReactNode,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react'

import cn from 'classnames/bind'
import {observer} from 'mobx-react-lite'
import {Pagination} from 'swiper'
import {Swiper, SwiperSlide} from 'swiper/react'

import PaystubTemplatePreview from 'components/blocks/forms/FormPayStubNew/PaystubTemplatePreview'
import Button from 'components/elements/Button'
import MobxTextInput from 'components/elements/fields/MobxTextInput'
import {Col, Row} from 'components/elements/grid'
import {posthogTrack} from 'src/utils'
import {useAuthStore, usePayStubStore} from 'src/utils/contexts/RootStore.context'
import {useMediaQuery} from 'src/utils/hooks/useMediaQuery'

import s from './PayStubChooseTemplateNew.module.scss'
import {TemplateThemesNew} from './TemplateThemesNew'
import {TemplateThumbnailIcon} from './TemplateThumbnailIcon'

import 'swiper/css'
import 'swiper/css/pagination'

const cx = cn.bind(s)

type TDefaultValues = {
	coNumber: string
	fileNumber: string
	deptNumber: string
	clockNumber: string
	vchrNumber: string
}

interface IPayStubChooseTemplateProps {
	className?: string
	children?: ReactNode | string
	templates: IPayStubTemplates[]
	activeTemplate: IPayStubTemplates | undefined
	handleTemplateTheme: (id: number, theme: IPayStubTemplates['theme']) => void
	handleActiveTemplate: (id: number) => void
	onChange?: FormEventHandler<HTMLDivElement>
}

const PayStubChooseTemplateNew = forwardRef<HTMLDivElement, IPayStubChooseTemplateProps>(
	(
		{
			className,
			children,
			templates,
			activeTemplate,
			handleTemplateTheme,
			handleActiveTemplate,
			...props
		},
		ref,
	) => {
		const formContent = useRef<HTMLDivElement>(null)
		const payStubStore = usePayStubStore()
		const {coNumber, fileNumber, deptNumber, clockNumber, vchrNumber} = payStubStore
		const [defaultValues] = useState<TDefaultValues>({
			coNumber,
			fileNumber,
			deptNumber,
			clockNumber,
			vchrNumber,
		})
		const [activeSlide, setActiveSlide] = useState(0)
		const isMobile = useMediaQuery('(max-width: 767px)')
		const authStore = useAuthStore()

		useEffect(() => {
			if (!authStore.hasActiveSubscription) {
				posthogTrack(`[PAYSTUB unsubscribed] Step 1 started`)
			}
		}, [authStore.hasActiveSubscription])

		useEffect(() => {
			posthogTrack(`[PAYSTUB] Step 1 started`)
		}, [])

		const handleFocusEvent = useCallback(
			(e: React.FocusEvent<HTMLInputElement>) => {
				const name = e.target.name
				if (e.target.value === '') {
					payStubStore[name as keyof TDefaultValues] = defaultValues[name as keyof TDefaultValues]
				}
			},
			[defaultValues, payStubStore],
		)

		const scrollToContent = () => {
			formContent.current?.scrollIntoView({
				behavior: 'smooth',
			})
		}

		return (
			<div className={cx('template', className)} ref={ref} {...props}>
				{children}
				<div className={cx('template--list', 'list')}>
					{templates.map(({id, isActive}) => (
						<button
							key={id}
							data-testid="list-item"
							className={cx('list__btn', isActive ? 'active' : '')}
							onClick={() => handleActiveTemplate(id)}
							type="button">
							<TemplateThumbnailIcon number={id} className={cx('list__btn-thumbnail')} />
						</button>
					))}
				</div>

				{activeSlide + 1 === activeTemplate?.id ? (
					<Button type="button" variant="secondary" className={cx('template__chosen')}>
						Chosen
					</Button>
				) : (
					<Button
						type="button"
						variant="secondary"
						className={cx('template__choose')}
						onClick={() => {
							handleActiveTemplate(activeSlide + 1)
							scrollToContent()
						}}>
						Choose this template
					</Button>
				)}

				<div className={cx('preview-slider')}>
					<Swiper
						slidesPerView={1}
						spaceBetween={8}
						modules={[Pagination]}
						pagination
						className={cx('preview-slider__swiper')}
						onSwiper={(swiper) => setActiveSlide(swiper.activeIndex)}
						onSlideChange={(swiper) => setActiveSlide(swiper.activeIndex)}>
						{templates.map((template, index) => (
							<SwiperSlide
								key={template.id}
								className={cx(
									'preview-slider__slide',
									index + 1 === activeTemplate?.id && 'preview-slider__slide-active',
								)}
								onClick={() => {
									handleActiveTemplate(activeSlide + 1)
									scrollToContent()
								}}>
								<PaystubTemplatePreview activeTemplate={template} />
							</SwiperSlide>
						))}
					</Swiper>
				</div>

				<div className={cx('template__content')} ref={formContent}>
					{isMobile
						? activeTemplate?.theme &&
						  activeSlide + 1 === activeTemplate?.id && (
								<TemplateThemesNew
									activeTemplate={activeTemplate}
									handleTemplateTheme={handleTemplateTheme}
								/>
						  )
						: activeTemplate?.theme && (
								<TemplateThemesNew
									activeTemplate={activeTemplate}
									handleTemplateTheme={handleTemplateTheme}
								/>
						  )}

					{activeTemplate?.id === 1 && (
						<div className={cx('template--fields')}>
							<Row className={cx('template__row')}>
								<Col>
									<MobxTextInput
										variant="new"
										size="large"
										wrapperClassName={cx('field')}
										label="CO. number"
										placeholder="yxx"
										store={payStubStore}
										name="coNumber"
										type="text"
										maxLength={3}
										onBlur={handleFocusEvent}
									/>
								</Col>

								<Col lg={6}>
									<MobxTextInput
										variant="new"
										size="large"
										wrapperClassName={cx('field')}
										label="File number"
										placeholder="630690"
										store={payStubStore}
										name="fileNumber"
										isNumber
										maxLength={6}
										onBlur={handleFocusEvent}
									/>
								</Col>

								<Col lg={6}>
									<MobxTextInput
										variant="new"
										size="large"
										wrapperClassName={cx('field')}
										label="DEPT.number"
										placeholder="015792"
										store={payStubStore}
										name="deptNumber"
										isNumber
										maxLength={6}
										onBlur={handleFocusEvent}
									/>
								</Col>

								<Col lg={6}>
									<MobxTextInput
										variant="new"
										size="large"
										wrapperClassName={cx('field')}
										label="Clock number"
										placeholder="95035"
										store={payStubStore}
										name="clockNumber"
										isNumber
										maxLength={5}
										onBlur={handleFocusEvent}
									/>
								</Col>
								<Col lg={6}>
									<MobxTextInput
										variant="new"
										size="large"
										wrapperClassName={cx('field')}
										label="VCHR Number"
										placeholder="0000007104"
										store={payStubStore}
										name="vchrNumber"
										isNumber
										maxLength={10}
										onBlur={handleFocusEvent}
									/>
								</Col>
							</Row>
						</div>
					)}

					{activeTemplate?.id === 2 && (
						<div className={cx('template--fields')}>
							<MobxTextInput
								variant="new"
								size="large"
								wrapperClassName={cx('field')}
								label={'Advice number'}
								placeholder="0000012837"
								store={payStubStore}
								name="vchrNumber"
								type="number"
								maxLength={10}
								onBlur={handleFocusEvent}
							/>
						</div>
					)}
				</div>
			</div>
		)
	},
)

PayStubChooseTemplateNew.displayName = 'PayStubChooseTemplate'

export default observer(PayStubChooseTemplateNew)
